import { HourMinutesSeconds } from "@suse-o11y/date-time/format/clock-time-format";
import { formatAbsoluteTime } from "@suse-o11y/date-time/format/format-absolute-time";
import type { Time } from "@suse-o11y/date-time/time";
import { Dialog, DialogClose, DialogTitle } from "@suse-o11y/dialog";
import { IconClose, IconErrorOutlined } from "@suse-o11y/icons";
import React from "react";
import { useSelector } from "react-redux";

import { Alert } from "~/components/alert";
import { Diff } from "~/components/code/diff";
import { Editor } from "~/components/code/editor";
import { DiffHeader } from "~/components/diff-header";

import { selectTimestampDate } from "../../../../../app/state/view/timeline/TimelineSelectors";
import type { Api, FullComponent } from "../../../../../common/openapi";
import { ErrorScreen } from "../../../../../components/react/full-center-view-layout/error-screen";
import { NotFound } from "../../../../../components/react/not-found/NotFound";
import { StateComponent } from "../../../../../components/react/states/StateComponent";
import { jsonToYaml } from "../../../../../generic/yamlUtils";

import { useQueryLastComponentChange } from "./useQueryLastComponentChange";
import { useQueryPodOwnerComponent } from "./useQueryPodOwnerComponent";

interface ComponentLastChangeDrawerProps extends Pick<DrawerBodyProps, "lastUpdateTimestamp" | "sourceProperties"> {
	component: FullComponent;
}

export const ComponentLastChangeDrawer: React.FC<ComponentLastChangeDrawerProps> = (props) => (
	<Dialog className="drawer-wide drawer/dialog">
		<DrawerContent {...props} />
	</Dialog>
);

const DrawerContent: React.FC<ComponentLastChangeDrawerProps> = ({
	component,
	sourceProperties,
	lastUpdateTimestamp,
}) => {
	const { isLoading, error, data: ownerComponent } = useQueryPodOwnerComponent(component.data.id!);

	if (error != null) {
		return (
			<>
				<header className="drawer-header">
					<IconErrorOutlined className="size-5 text-core-text-alert-primary" />

					<DialogTitle className="drawer-title">Failed to load last change information</DialogTitle>

					<DialogClose aria-label="Close" className="button button-square ml-auto">
						<IconClose className="size-3" />
					</DialogClose>
				</header>

				<section className="drawer-body">
					<ErrorScreen error={error} noShadow />
				</section>
			</>
		);
	}

	if (isLoading) {
		return (
			<>
				<header className="drawer-header">
					<DialogTitle className="drawer-title skeleton w-2/3" />

					<DialogClose aria-label="Close" className="button button-square ml-auto">
						<IconClose className="size-3" />
					</DialogClose>
				</header>

				<section className="drawer-body">
					<div className="skeleton h-full" />
				</section>
			</>
		);
	}

	return (
		<>
			<header className="drawer-header">
				<DialogTitle className="drawer-title">
					{ownerComponent == null ? (
						<StateComponent component={component} displayName displayTypeName typeOfData="Last change" />
					) : (
						<StateComponent
							component={ownerComponent}
							displayName
							displayTypeName
							typeOfData="Last change on the pod's owner"
						/>
					)}
				</DialogTitle>

				<DialogClose aria-label="Close" className="button button-square ml-auto">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<DrawerBody
				componentId={ownerComponent?.id ?? component.data.id!}
				lastUpdateTimestamp={lastUpdateTimestamp}
				sourceProperties={sourceProperties}
			/>
		</>
	);
};

interface DrawerBodyProps {
	componentId: number;
	sourceProperties: Api.ExternalComponent.SourceProperties;
	lastUpdateTimestamp: undefined | Time;
}

const DrawerBody: React.FC<DrawerBodyProps> = ({ componentId, sourceProperties, lastUpdateTimestamp }) => {
	const { isLoading, error, diff } = useQueryLastComponentChange(componentId);

	const source = React.useMemo(() => jsonToYaml(sourceProperties), [sourceProperties]);

	if (error != null) {
		return (
			<section className="drawer-body">
				<ErrorScreen error={error} noShadow />
			</section>
		);
	}

	if (isLoading) {
		return (
			<section className="drawer-body">
				<div className="skeleton h-full" />
			</section>
		);
	}

	if (diff != null) {
		return (
			<section className="drawer-body join join-vertical join-space-0">
				<DiffHeader className="join-item" modifiedTimestamp={diff.newTimestamp} originalTimestamp={diff.oldTimestamp} />
				<Diff className="join-item" height="auto" language="yaml" modified={diff.new} original={diff.old} />
			</section>
		);
	}

	if (!source) {
		return (
			<section className="drawer-body">
				<DiffNotFound />
			</section>
		);
	}

	return (
		<section className="drawer-body">
			<Alert>
				We couldn’t find any other configuration prior to {formatAbsoluteTime(lastUpdateTimestamp).toString()}. Most
				likely the component has not been changed or the last change is older than six months.
			</Alert>

			<Editor className="mt-4" height="100%" language="yaml" readOnly value={source} />
		</section>
	);
};

const DiffNotFound: React.FC = () => {
	const timestamp = useSelector(selectTimestampDate);
	const timestampFormatted = formatAbsoluteTime(timestamp, {
		clockTimeFormat: HourMinutesSeconds,
	}).toString();

	return (
		<NotFound header="We couldn’t find any change">
			There are no configuration changes in the last six months before {timestampFormatted}.
			<br />
			Most likely the component has not been changed or the last change is older than six months.
		</NotFound>
	);
};
